import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import type { UniBoxConfig, UniBoxEntity } from './types';
import { getStatusDef } from './taxonomy';
import { LeadAvatar } from './LeadAvatar';

// Outcome buckets that tint the SequenceDots row. Shared between the kanban
// lead card and the drawer SequenceBoard so both surfaces agree on colour.
const RED_OUTCOMES = new Set(['bounced', 'do_not_contact', 'do_not_contact_request', 'unsubscribed', 'unsubscribe', 'dnc']);
const YELLOW_OUTCOMES = new Set(['out_of_office', 'no_response', 'no_show', 'nurture']);

const formatLabelHelper = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * SequenceDots — three dots representing the Icebreaker / Follow-up / Last-
 * message steps of an outreaching lead. SHARED by the kanban lead card and the
 * drawer's SequenceBoard header so the two surfaces stay visually consistent.
 *
 *   white  = step sent & delivered
 *   grey   = step pending
 *   yellow = caution outcome (out-of-office, no response, no-show, nurture)
 *   red    = blocking outcome (bounced, do-not-contact, unsubscribed)
 *
 * Renders null for non-outreaching leads (dots are an outreach indicator only)
 * and when there are no visible steps.
 */
export interface SequenceDotsProps {
  entity: UniBoxEntity;
  /** Tailwind size classes for each dot (default h-1.5 w-1.5). */
  dotSize?: string;
  /** Extra classes for the wrapping span. */
  className?: string;
}

export const SequenceDots: React.FC<SequenceDotsProps> = ({ entity, dotSize = 'h-1.5 w-1.5', className = '' }) => {
  if (entity.status !== 'outreaching') return null;
  const outcomeKey = typeof entity.outcome === 'string' ? entity.outcome.trim() : '';
  const isRed = RED_OUTCOMES.has(outcomeKey);
  const isYellow = !isRed && YELLOW_OUTCOMES.has(outcomeKey);
  const prog = entity.sequenceProgress?.[0];
  const total = prog?.total ?? 3;
  const steps = entity.sequenceSteps || [];
  const dotState = Array.from({ length: 3 }, (_, i) => {
    const stepNum = i + 1;
    if (stepNum > total) return 'hidden' as const;
    const step = steps.find((s) => s.step === stepNum);
    const sent = step ? Boolean(step.sentAt) : stepNum <= (prog?.sent ?? 0);
    return sent ? ('sent' as const) : ('pending' as const);
  });
  if (dotState.every((d) => d === 'hidden')) return null;
  const dotClass = (sent: boolean) => {
    if (isRed) return 'bg-red-500';
    if (isYellow) return sent ? 'bg-amber-400' : 'bg-amber-400/25';
    return sent ? 'bg-white/68' : 'bg-white/15';
  };
  const toneLabel = isRed ? 'blocked' : isYellow ? 'at risk' : 'healthy';
  const tooltip = `Icebreaker · Follow-up · Last message\n${toneLabel}${outcomeKey ? ` · ${formatLabelHelper(outcomeKey)}` : ''}`;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 ${className}`} title={tooltip}>
      {dotState.map((d, i) => (
        <span key={i} className={`rounded-full ${dotSize} ${d === 'hidden' ? 'opacity-0' : dotClass(d === 'sent')}`} />
      ))}
    </span>
  );
};
const getChannelMeta = (channel: string): { label: string; icon: string; accent: string } => {
  const map: Record<string, { label: string; icon: string; accent: string }> = {
    whatsapp: { label: 'WhatsApp', icon: 'bx:bxl-whatsapp', accent: 'text-emerald-300' },
    email: { label: 'Email', icon: 'bx:envelope', accent: 'text-cyan-200' },
    sms: { label: 'SMS', icon: 'bx:message-rounded', accent: 'text-indigo-200' },
    call: { label: 'Call', icon: 'bx:phone-call', accent: 'text-amber-200' },
    linkedin: { label: 'LinkedIn', icon: 'bx:bxl-linkedin', accent: 'text-sky-200' },
    instagram: { label: 'Instagram', icon: 'bx:bxl-instagram', accent: 'text-pink-200' },
    x: { label: 'X', icon: 'bx:bxl-twitter', accent: 'text-slate-100' },
    messenger: { label: 'Messenger', icon: 'bx:bxl-messenger', accent: 'text-blue-200' },
    telegram: { label: 'Telegram', icon: 'bx:bxl-telegram', accent: 'text-sky-200' },
  };
  return map[channel] || { label: channel, icon: 'bx:message-rounded', accent: 'text-gray-200' };
};

/**
 * SequenceBoard — the outreach preview. For an outreaching lead, shows each
 * channel's multi-step sequence as sent/pending chips ("2/3"), the upcoming
 * message body, and a secondary supervisor Edit affordance.
 *
 * The human is a SUPERVISOR here, not the author — so Edit is deliberately
 * understated and clearly labelled "supervisor". Write-back to the backend is
 * a flagged follow-up; for now editing is local/visual only.
 */
export interface SequenceBoardProps {
  config: UniBoxConfig;
  entity: UniBoxEntity;
  onClose?: () => void;
  /** Open the lead's full profile (Leads Intelligence detail). */
  onOpenLead?: () => void;
}

export const SequenceBoard: React.FC<SequenceBoardProps> = ({ config, entity, onClose, onOpenLead }) => {
  const def = getStatusDef(entity.status);
  // Group the full sequence templates by channel.
  const byChannel = React.useMemo(() => {
    const map = new Map<string, { step: number; subject?: string; body: string; sentAt: string | null }[]>();
    (entity.sequenceSteps || []).forEach((s) => {
      if (!map.has(s.channel)) map.set(s.channel, []);
      map.get(s.channel)!.push({ step: s.step, subject: s.subject, body: s.body, sentAt: s.sentAt });
    });
    // Ensure every entity channel appears even if no templates (so the user
    // sees the channel is in the plan, sequence not yet generated).
    entity.channels.forEach((c) => { if (!map.has(c)) map.set(c, []); });
    return Array.from(map.entries());
  }, [entity.sequenceSteps, entity.channels]);

  const [editing, setEditing] = useState<{ channel: string; step: number } | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Active channel tab — defaults to the lead's first channel (or the most
  // advanced one). Persists while the entity stays selected.
  const [activeChannel, setActiveChannel] = useState<string>(byChannel[0]?.[0] || entity.channels[0] || '');
  // Reset the active channel when the entity changes.
  useEffect(() => { setActiveChannel(byChannel[0]?.[0] || entity.channels[0] || ''); }, [entity.lvl1_id]);
  const activeChannelSteps = byChannel.find(([c]) => c === activeChannel)?.[1] || [];

  // Channel tab strip is a horizontal carousel: never wraps to a second row,
  // scrolls left/right instead. Arrows appear only when there's overflow.
  const channelScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateChannelScrollState = React.useCallback(() => {
    const el = channelScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);
  useEffect(() => {
    // Evaluate overflow once the strip mounts / the entity changes.
    updateChannelScrollState();
  }, [byChannel, updateChannelScrollState]);
  const scrollChannelsBy = (dir: 1 | -1) => {
    const el = channelScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const subDef = config.substatusByStage[entity.status]?.find((s) => s.key === entity.substatus);
  const outcomeDef = config.outcomes.find((o) => o.key === entity.outcome);
  // Role / position — forwarded on the entity (same field the Kanban card reads).
  const roleLabel = typeof entity.lead_role === 'string' ? entity.lead_role.trim() : '';
  // Title preference: company name first (mirrors Lead Intelligence), falling
  // back to the person name when no company is enriched.
  const headerTitle = entity.company || entity.client_name;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-[linear-gradient(180deg,rgba(8,9,12,0.9),rgba(5,6,9,0.98))]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.035),transparent_55%)]" />
      <div className="relative flex min-h-0 flex-col">
        {/* Header — avatar + company logo (same composite as Lead Intelligence),
            company name as the title, person name + role beneath. */}
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <LeadAvatar entity={entity} avatarSize="h-12 w-12" logoSize="h-7 w-7" />
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-white/45">Send plan</p>
              <h3 className="mt-0.5 truncate text-base font-bold leading-tight text-white">{headerTitle}</h3>
              {entity.company && entity.client_name && entity.client_name !== entity.company && (
                <p className="mt-0.5 truncate text-xs font-semibold text-white/55">{entity.client_name}</p>
              )}
              {roleLabel && <p className="mt-0.5 truncate text-[11px] font-medium text-white/45">{roleLabel}</p>}
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.045] px-2 py-0.5 text-[10px] font-bold text-white/62">
                  <span className={`h-1.5 w-1.5 rounded-full ${def.dot}`} />
                  {def.label}
                </span>
                {subDef && <span className="text-[11px] font-semibold text-white/55">{subDef.label}</span>}
                {outcomeDef && <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] px-2 py-0.5 text-[10px] font-semibold text-white/50"><Icon icon="bx:filter-alt" className="h-3 w-3" />{outcomeDef.label}</span>}
                {/* Sequence dots — same indicator as the kanban lead card, so the
                    drawer and the board read identically. Outreaching leads only. */}
                <SequenceDots entity={entity} dotSize="h-2 w-2" className="ml-1" />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {onOpenLead && (
              <button
                type="button"
                onClick={onOpenLead}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.045] px-2.5 py-1.5 text-[11px] font-bold text-white/70 transition-all hover:bg-white/[0.075] hover:text-white"
                title="Open full lead details"
              >
                <Icon icon="bx:user-detail" className="h-3.5 w-3.5" />
                Lead details
              </button>
            )}
            {onClose && (
              <button type="button" onClick={onClose} aria-label="Close preview" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/45 transition-colors hover:bg-white/[0.06] hover:text-white">
                <Icon icon="bx:x" className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
          {/* Channel tabs — switch to see each channel's full sequence. Styled as
              distinct section tabs and laid out as a NON-WRAPPING carousel so the
              Email/LinkedIn/WhatsApp tabs stay on one row and scroll left/right
              when they overflow (with arrow controls). */}
          <div className="mb-4 shadow-[inset_0_-1px_0_rgba(255,255,255,0.024)]">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => scrollChannelsBy(-1)}
                aria-label="Scroll channels left"
                tabIndex={-1}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/55 transition-all ${
                  canScrollLeft ? 'hover:bg-white/[0.07] hover:text-white' : 'pointer-events-none opacity-0'
                }`}
              >
                <Icon icon="bx:chevron-left" className="h-4 w-4" />
              </button>
              <div
                ref={channelScrollRef}
                onScroll={updateChannelScrollState}
                className="flex min-w-0 flex-1 items-end gap-1 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {byChannel.map(([channel, steps]) => {
                  const meta = getChannelMeta(channel);
                  const total = steps.length || 3;
                  const sent = steps.filter((s) => s.sentAt).length;
                  const isActive = activeChannel === channel;
                  return (
                    <button
                      key={channel}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveChannel(channel)}
                      className={`group relative inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black uppercase tracking-[0.06em] transition-all duration-150 ${
                        isActive
                          ? 'bg-white/[0.075] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                          : 'text-white/45 hover:bg-white/[0.035] hover:text-white/80'
                      }`}
                    >
                      <Icon icon={meta.icon} className={`h-3.5 w-3.5 transition-colors ${isActive ? meta.accent : 'text-white/45 group-hover:text-white/70'}`} />
                      {meta.label}
                      <span className={`ml-0.5 inline-flex items-center gap-1 text-[10px] font-bold ${isActive ? 'text-white/68' : 'text-white/35 group-hover:text-white/55'}`}>
                        <span className="flex items-center gap-0.5">
                          {Array.from({ length: total }).map((_, i) => (
                            <span key={i} className={`h-1 w-1 rounded-full ${i < sent ? 'bg-white/65' : 'bg-white/15'}`} />
                          ))}
                        </span>
                        {sent}/{total}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => scrollChannelsBy(1)}
                aria-label="Scroll channels right"
                tabIndex={-1}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/55 transition-all ${
                  canScrollRight ? 'hover:bg-white/[0.07] hover:text-white' : 'pointer-events-none opacity-0'
                }`}
              >
                <Icon icon="bx:chevron-right" className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Selected channel — the FULL sequence (all messages), each with its
              sent/pending state. The upcoming one is highlighted; others are
              readable so the supervisor sees the whole plan by channel. */}
          {activeChannelSteps.length === 0 ? (
            <p className="rounded-lg bg-white/[0.02] px-3 py-4 text-center text-[11px] text-white/35">Sequence not generated yet for {getChannelMeta(activeChannel).label}.</p>
          ) : (
            <div className="space-y-2.5">
              {activeChannelSteps.map((s) => {
                const isSent = Boolean(s.sentAt);
                const isNext = !isSent && activeChannelSteps.filter((x) => !x.sentAt)[0]?.step === s.step;
                const draftKey = `${activeChannel}_${s.step}`;
                const isEditing = editing?.channel === activeChannel && editing.step === s.step;
                return (
                  <div
                    key={s.step}
                    className={`rounded-xl p-3 transition-colors ${
                      isSent ? 'bg-white/[0.035]' : isNext ? 'bg-white/[0.065] shadow-[inset_3px_0_0_rgba(255,255,255,0.28)]' : 'bg-white/[0.018]'
                    }`}
                  >
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                        isSent ? 'text-white/66' : isNext ? 'text-white/88' : 'text-white/40'
                      }`}>
                        {isSent ? <Icon icon="bx:check-circle" className="h-3.5 w-3.5" /> : isNext ? <Icon icon="bx:time-five" className="h-3.5 w-3.5" /> : <Icon icon="bx:circle" className="h-3.5 w-3.5" />}
                        {config.sequence_labels?.[s.step] || `Message ${s.step}`}
                        {isSent && s.sentAt && <span className="font-medium normal-case tracking-normal text-white/35">· sent {new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(s.sentAt))}</span>}
                        {isNext && <span className="font-medium normal-case tracking-normal text-white/58">· upcoming</span>}
                      </span>
                      {!isEditing && (
                        <button
                          type="button"
                          onClick={() => setEditing({ channel: activeChannel, step: s.step })}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-white/35 transition-colors hover:text-white/70"
                          title="Supervisor edit (visual only until write-back is wired)"
                        >
                          <Icon icon="bx:pencil" className="h-3 w-3" />
                          Edit
                        </button>
                      )}
                    </div>
                    {s.subject && <p className="mb-1 text-[11px] font-bold text-white/70">{s.subject}</p>}
                    {isEditing ? (
                      <div className="space-y-1.5">
                        <textarea
                          value={drafts[draftKey] ?? s.body}
                          onChange={(e) => setDrafts((d) => ({ ...d, [draftKey]: e.target.value }))}
                          rows={3}
                          className="w-full resize-none rounded-lg bg-white/[0.045] px-2 py-1.5 text-xs text-white outline-none focus:bg-white/[0.065]"
                        />
                        <div className="flex items-center justify-end gap-1.5">
                          <button type="button" onClick={() => { setEditing(null); setDrafts((d) => { const n = { ...d }; delete n[draftKey]; return n; }); }} className="rounded-md px-2 py-1 text-[10px] font-bold text-white/45 hover:text-white/75">Cancel</button>
                          <button type="button" onClick={() => setEditing(null)} className="rounded-md bg-white px-2 py-1 text-[10px] font-bold text-black hover:bg-gray-100">Save <span className="opacity-60">(local)</span></button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs leading-5 text-gray-300">{drafts[draftKey] ?? s.body}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="flex items-center justify-center gap-1.5 pt-3 text-[10px] text-white/25">
            <Icon icon="bx:info-circle" className="h-3 w-3" />
            You're supervising the autopilot — edits are visual until write-back is wired.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SequenceBoard;
